import React, { useMemo, useEffect } from 'react';
import { useIntl, useSelector, getLocale, history } from 'umi';
import StoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore';
import ExportTable from '@intelligence/components/ExportTable';
import { LANGUAGE_CN } from '@intelligence/_config/config';
import SvgIcon from '@home/components/SvgIcon';
import PCStoreRankTopFilter from '@/pages/intelligence/pages/StoreRanks/PCGlobal/components/PCStoreRankTopFilter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useStoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore/_hooks/useStoreRankTopCore';
import { getValidGranularity } from '@/pages/intelligence/utils/commonUtils';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import { PC_SOURCE_STEAM_TOP_SELLERS } from '@/pages/intelligence/_config/storeRank/config';
import {
  RANGE_TIME as FILTER_RANGE_TIME,
  COMPOSED_COUNTRY_SELECT as FILTER_COMPOSED_COUNTRY_SELECT,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useStoreTopRegionOptionsMap from '@intelligence/v3/_hooks/useStoreTopRegionOptionsMap';
import { getSortedTopsKeys } from '@/pages/intelligence/_config/storeRank/utils';
import { STEAM_PLATFORM, PC_PLATFORMS } from '@intelligence/v3/constants/topcharts';

import '@/pages/intelligence/assets/scss/StoreRanks/PCGlobal/index.scss';
import './index.scss';

import type { IBaseProps, IDateTypely } from '@/pages/intelligence/types/common';
import type { IStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';

const ENTITY_TYPE = 'pc';
const GAME_ID_KEY = 'app_id';
const BUSINESS_TYPE = 'pc-steam-store-rank-top';
const DOWNLOAD_KEY = 'mobileStoreRankTop';
const DOWNLOAD_EXCEL_LOG_CFG_KEY = 'pcSteamStoreRankTopDownloadExcel';
const MORE_PAGE_PATH = '/intelligence/storeRanks/PCGlobal/more/steam';

// 榜单支持的时间粒度
const GRANULARITIES: IDateTypely[] = ['daily', 'weekly'];

// 默认筛选器数据，用于reset时、兜底
const DEFAULT_FILTER: IStoreRankTopFilter = {
  store: 'steam',
  region_type: 'market',
  regions: ['global'],
  genre: [],
  sub_genre: [],
  genre_source: 'iegg',
  iegg_genre_req: [],
  granularity: 'daily',
  start_date: '',
  end_date: '',
};

type IPCStoreRankTopSteamProps = IBaseProps;

const PCStoreRankTopSteam: React.FC<IPCStoreRankTopSteamProps> = (props) => {
  const {} = props;

  const { formatMessage } = useIntl();

  const { filterExtend } = useFilterExtend({});
  const filterState = useSelector((rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE]);
  // 是否操作过筛选器，判断是否继承的条件
  const isOperate = filterState?.isOperate || false;

  const { regionsOptions: regionOptions } = useStoreTopRegionOptionsMap(STEAM_PLATFORM, {
    entityType: ENTITY_TYPE,
    platforms: PC_PLATFORMS,
  });

  // 初始化筛选器数据，合并继承值、默认值
  const initialFilter = useMemo<IStoreRankTopFilter>(() => {
    if (!isOperate) return DEFAULT_FILTER;

    const filterExtendTmp = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtend);
    const rangeTimeFilterExtend = filterExtendTmp[FILTER_RANGE_TIME];
    const composedCountryExtend = filterExtendTmp?.[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const granularityTmp = rangeTimeFilterExtend?.granularity || DEFAULT_FILTER?.granularity;
    const initialGranularity = getValidGranularity(granularityTmp, GRANULARITIES);
    const initialStartDate = rangeTimeFilterExtend?.start_date || DEFAULT_FILTER?.start_date;
    const initialEndDate =
      initialGranularity === 'weekly'
        ? rangeTimeFilterExtend?.end_date || DEFAULT_FILTER?.end_date
        : initialStartDate;
    const initialRegions =
      composedCountryExtend?.countries?.length === 1
        ? composedCountryExtend?.countries
        : DEFAULT_FILTER.regions;

    return {
      ...DEFAULT_FILTER,
      regions: initialRegions,
      granularity: initialGranularity,
      start_date: initialStartDate,
      end_date: initialEndDate,
    };
  }, [isOperate, filterExtend]);

  const noDataRenderer = (payload: { source: string; lastDate: string }) => {
    const { source, lastDate } = payload || {};
    if (filter?.granularity === 'weekly' && source !== PC_SOURCE_STEAM_TOP_SELLERS) {
      return (
        <div className="dbi-storeranks-topcommcard-nodata">
          {formatMessage({ id: 'dbi_store_rank_no_data_only_daily' })}
        </div>
      );
    }
    return (
      <div className="dbi-storeranks-topcommcard-nodata">
        {formatMessage({ id: 'dbi_store_rank_no_data_under_current_selections' })}
        {lastDate && (
          <>
            <br />
            {formatMessage({ id: 'dbi_store_rank_last_updated_on' }, { date: lastDate })}
          </>
        )}
      </div>
    );
  };

  const {
    // 榜单数据加载状态
    isLoading,
    // 榜单数据
    tops,
    // 筛选器数据
    filter,
    // 页面初始化
    onPageInit,
    // 筛选器confirm的回调
    onFilterConfirm,
    // 筛选器reset的回调
    onFilterReset,
    // 榜单卡片点击游戏的回调
    onCardGameClick,
    // 榜单卡片点击标题的回调
    onGoToStoreRankMore,
    getDetailGamePath,
  } = useStoreRankTopCore({
    entityType: ENTITY_TYPE,
    // 游戏id的取值字段
    gameIdKey: GAME_ID_KEY,
    // 榜单卡片点击标题 跳转榜单下钻页的 路由path
    morePagePath: '/intelligence/storeRanks/PCGlobal/more/steam',
    // 页面打点配置的key
    pageLogCfgKey: 'pcStoreRankTopSteam',
    isOperate,
    // 初始化筛选器数据
    initialFilter,
    // 查询榜单数据 初始化的分页大小
    initialPageSize: 10,
  });

  useEffect(() => {
    onPageInit(initialFilter);
  }, []);

  const locale = getLocale();

  const downloadParams = useMemo(() => {
    const store = DEFAULT_FILTER.store;
    const granularity = filter?.granularity || DEFAULT_FILTER.granularity;
    return {
      entity_type: ENTITY_TYPE,
      store,
      region_type: filter?.region_type || DEFAULT_FILTER.region_type,
      regions: filter?.regions || DEFAULT_FILTER.regions,
      genre: filter?.genre || [],
      sub_genre: filter?.sub_genre || [],
      genre_source: filter?.genre_source || DEFAULT_FILTER.genre_source,
      iegg_genre_req: filter?.iegg_genre_req || [],
      granularity,
      start_date: filter?.start_date || '',
      end_date: filter?.end_date || '',
      rankingSources:
        getSortedTopsKeys(ENTITY_TYPE, store, granularity) || [],
    };
  }, [filter]);

  const downloadFileName = useMemo(() => {
    const startDate = filter?.start_date || '';
    const endDate = filter?.end_date || '';
    return `Store Ranks_PC_Steam_Top_${startDate}_${endDate}`;
  }, [filter?.start_date, filter?.end_date]);

  const handleClickMultiCountry = () => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('source', encodeURIComponent(PC_SOURCE_STEAM_TOP_SELLERS));
    urlSearchParams.append('isCheckAllCountries', '1');
    history.push(`${MORE_PAGE_PATH}?${urlSearchParams.toString()}`);
  };

  const multiCountryText = (
    <span className="dbi-storerank-single-country-select-footer">
      {formatMessage({ id: 'dbi_store_rank_single_country_select_footer_btn_text' })}
      <SvgIcon type="aigc-jump" />
    </span>
  );

  return (
    <StoreRankTopCore
      className="dbi-steam dbi-pc-store-rank-top dbi-pc-store-rank-top-steam"
      entityType="pc"
      showDownloadTips
      inlineDownload
      gameIdKey={GAME_ID_KEY}
      isLoading={isLoading}
      loadingContentStyle={{ height: 'calc(100vh - 472px)' }}
      tops={tops}
      noDataRenderer={noDataRenderer}
      filter={
        <PCStoreRankTopFilter
          isOperate={isOperate}
          typeList={['date', 'week']}
          granularity={filter?.granularity}
          startDate={filter?.start_date}
          endDate={filter?.end_date}
          isRegionVisible={true}
          regionOptions={regionOptions}
          overallGroup={{
            options: [{ value: 'global', text: formatMessage({ id: 'dbi_global' }) }],
          }}
          defaultRegions={DEFAULT_FILTER.regions}
          multiCountryText={multiCountryText}
          onClickMultiCountry={handleClickMultiCountry}
          onConfirm={onFilterConfirm}
          onReset={onFilterReset}
        />
      }
      exportTable={
        <ExportTable
          entityType={ENTITY_TYPE}
          // @ts-ignore
          businessType={BUSINESS_TYPE}
          downloadLogConfigKey={DOWNLOAD_EXCEL_LOG_CFG_KEY}
          isDisableDownload={isLoading}
          total={tops?.length ?? 0}
          tableParams={downloadParams}
          newStyleFileName={downloadFileName}
          isShowMaxDownloadRowsTips={false}
          downloadModalSize={locale === LANGUAGE_CN ? 580 : 686}
          useDownloadAmountType={true}
          useWebDownload={true}
          downloadKey={DOWNLOAD_KEY}
          regionOptions={regionOptions}
        />
      }
      onCardTitleClick={onGoToStoreRankMore}
      onCardGameClick={onCardGameClick}
      getDetailGamePath={getDetailGamePath}
    />
  );
};

export default PCStoreRankTopSteam;
